// Turn dashboard API data into the rows/cards the dashboard components display.
import {
  changeNote,
  changeTone,
  formatCurrency,
  formatDate,
  formatPercent,
  getInitials,
} from "@/utils/formatters";

const VS_LAST_MONTH = "vs last month";

export function buildKpiCards(kpi) {
  const profitToday = Number(kpi.profit_today || 0);
  const lowStock = Number(kpi.low_stock_count || 0);
  const outOfStock = Number(kpi.out_of_stock_count || 0);
  return [
    {
      title: "Today's Sales",
      value: formatCurrency(kpi.revenue_today),
      note: changeNote(kpi.revenue_change_percent, "vs yesterday"),
      noteTone: changeTone(kpi.revenue_change_percent),
      icon: "trending-up",
      tone: "tone-orange",
    },
    {
      title: "Today's Profit",
      value: formatCurrency(kpi.profit_today),
      note: `${profitToday < 0 ? "↓ Loss" : "↑ Profit"} · Margin ${formatPercent(kpi.margin_today_percent)}`,
      noteTone: profitToday < 0 ? "metric-note-danger" : "metric-note-up",
      icon: "pie-chart",
      tone: "tone-navy",
    },
    {
      title: "Stock Alerts",
      value: `${lowStock} low stock`,
      note: `${lowStock} low stock · ${outOfStock} out of stock`,
      noteTone: outOfStock > 0 ? "metric-note-danger" : "metric-note-neutral",
      icon: "alert-circle",
      tone: "tone-teal",
    },
    {
      title: "Today's Purchase",
      value: formatCurrency(kpi.purchase_today),
      note: changeNote(kpi.purchase_change_percent, "vs yesterday"),
      noteTone: changeTone(kpi.purchase_change_percent),
      icon: "shopping-bag",
      tone: "tone-blue",
    },
  ];
}

function positionCard(title, value, changePercent, icon, extra = {}) {
  return {
    title,
    value: formatCurrency(value),
    note: changeNote(changePercent, VS_LAST_MONTH),
    noteTone: changeTone(changePercent),
    icon,
    ...extra,
  };
}

// Stock value, credits, bank, cash and net asset, each with its change vs last month.
export function buildFinancialPositionCards(fp) {
  return [
    {
      ...positionCard("Current Stock Value", fp.stock_value, fp.stock_value_change_percent, "box"),
      note: `Buy Price · ${changeNote(fp.stock_value_change_percent, VS_LAST_MONTH)}`,
    },
    positionCard("Credit Payable", fp.credit_payable, fp.credit_payable_change_percent, "file-minus"),
    positionCard("Credit Receivable", fp.credit_receivable, fp.credit_receivable_change_percent, "user-check"),
    positionCard("Bank Account Balance", fp.bank_balance, fp.bank_balance_change_percent, "briefcase"),
    positionCard("Cash in Shop", fp.cash_in_shop, fp.cash_in_shop_change_percent, "dollar-sign"),
    positionCard("Net Asset", fp.net_asset, fp.net_asset_change_percent, "pie-chart", {
      formula: "Stock − Payable + Receivable + Bank + Cash",
    }),
  ];
}

const profitTone = (value) => (Number(value) < 0 ? "danger-text" : "success-text");

// Business / Profit / Expense / Credits, today vs this month.
export function buildPerformanceRows(ps) {
  return [
    {
      label: "Business",
      icon: "shopping-cart",
      tone: "tone-orange-chip",
      today: formatCurrency(ps.business_today),
      month: formatCurrency(ps.business_month),
      todayTone: "",
      monthTone: "",
    },
    {
      label: "Profit",
      icon: "trending-up",
      tone: "tone-teal-chip",
      today: formatCurrency(ps.profit_today),
      month: formatCurrency(ps.profit_month),
      todayTone: profitTone(ps.profit_today),
      monthTone: profitTone(ps.profit_month),
    },
    {
      label: "Expense",
      icon: "trending-down",
      tone: "tone-red-chip",
      today: formatCurrency(ps.expense_today),
      month: formatCurrency(ps.expense_month),
      todayTone: "danger-text",
      monthTone: "danger-text",
    },
    {
      label: "Credits",
      icon: "file-text",
      tone: "tone-purple-chip",
      today: formatCurrency(ps.credits_today),
      month: formatCurrency(ps.credits_month),
      todayTone: "",
      monthTone: "",
      monthNote: "total this month",
    },
  ];
}

export function buildSlowMovingRows(slowMoving) {
  return slowMoving.map((row) => ({
    ...row,
    latest_purchase_date: formatDate(row.latest_purchase_date),
  }));
}

// Recent sales as rows for the "Recent Transactions" list (at most 5).
export function buildRecentSaleRows(recentBills) {
  return recentBills.slice(0, 5).map((row, index) => {
    const customer = row.customername || "Walk-in Customer";
    const paymentMode = row.paymentmode || "-";
    return {
      key: row.saleid || `sale-${index}`,
      date: formatDate(row.billdate),
      customer,
      initials: getInitials(customer),
      reference: row.billno ? `#${row.billno}` : "#SALE",
      status: paymentMode === "-" ? "Completed" : paymentMode,
      statusClass: "status-completed",
      total: formatCurrency(row.netamount),
    };
  });
}

// Out-of-stock items first, then low-stock items.
export function buildStockAlertRows(stockAlerts) {
  const withKey = (prefix) => (row) => ({ ...row, rowkey: `${prefix}-${row.itemid}-${row.locationid || "all"}` });
  return [
    ...(stockAlerts.out_of_stock_items || []).map(withKey("out")),
    ...(stockAlerts.low_stock_items || []).map(withKey("low")),
  ];
}

export function chequeStatusClass(status) {
  const value = String(status || "").toLowerCase();
  if (value === "cleared") return "status-completed";
  if (value === "bounced" || value === "cancelled") return "status-draft";
  return "status-pending";
}
